import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useState, useContext, useEffect, useRef } from 'react'

import { Checkbox } from '@mui/material'
import { countriesCurrencies } from '../lib'
import { /* Document, */ FormError, UserSessionType } from '../types'
import '../styles/upload-document-process.css'
import { useSocket } from '../hooks/use-socket'
import Dialog from '../components/common/dialog'
import { requestAccessToken } from '../api/auth.api'
import { AuthContext } from '../contexts/auth.context'
import { UserContext } from '../contexts/user.context'
import { AppStateContext } from '../contexts/app-state.context'
import { DocumentsContext } from '../contexts/documents.context'
import ProcessStepOne from '../components/upload-document-process/process-step-one'
// import { handleCheckIfDatabaseExists, handleGetDataFromTable } from '../utils/db/common-methods'
import {getUploadDocumentPreSignedURL, uploadDocumentToS3, saveDocumentToAnalyze, getDownloadDocumentPreSignedURL, resolveDocumentsAccessToken } from '../api/documents.api'
import { getBatchByName } from '../api/app-queries.api'

const UploadDocumentProcess = () => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
  const currency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

  const navigate = useNavigate()
  const { t, i18n } = useTranslation(['common'])
  const { userSession } = useContext(UserContext)
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { appDataSocket, isAppDataSocketInitialized } = useSocket()
  const { updateLoadingStatus, defaultPocketIdSelected } = useContext(AppStateContext)
  const { uploadingDocumentId, setUploadingDocument, updateUploadingDocumentId } = useContext(DocumentsContext)

  const alreadyRedirectedRef = useRef<boolean>(false)
  const currentUploadingDocumentIdRef = useRef<string | null>(null)

  const [errors, setErrors] = useState<FormError>({})
  const [documentUploaded, setDocumentUploaded] = useState(false)
  const [nitWarningAccepted, setNitWarningAccepted] = useState(false)
  const [openAnalyzingModal, setOpenAnalyzingModal] = useState(false)
  const [selectedPocketId, setSelectedPocketId] = useState<string>('')
  const [documentFileToUpload, setDocumentFileToUpload] = useState<Array<File>>([])
  const [nitVerificationCodeWarningChecked, setNitVerificationCodeWarningChecked] = useState(false)
  const [openNitVerificationCodeWarningModal, setOpenNitVerificationCodeWarningModal] = useState(false)

  useEffect(() => {
    if (!appDataSocket) return

    appDataSocket.on('updateBatch', async (dataFromSocket: any) => {
      if (alreadyRedirectedRef.current === false && String(currentUploadingDocumentIdRef.current) === String(dataFromSocket.batchId)) {
        alreadyRedirectedRef.current = true
        navigate('/documents-to-review', { state: { previousPath: 'home' } })
      }
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appDataSocket, isAppDataSocketInitialized])

  useEffect(() => {
    // handleSetDocumentsCollection()
    const nitWarningState = globalThis.localStorage.getItem('nitWarningAccepted')

    if (nitWarningState && nitWarningState === 'true') {
      setNitWarningAccepted(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (userSession?.role === 'super-admin-client' && userSession?.adminOfPockets?.length === 1) {
      setSelectedPocketId(String(userSession.adminOfPockets[0]))
    }
  }, [userSession])

  useEffect(() => {
    if (uploadingDocumentId) {
      currentUploadingDocumentIdRef.current = uploadingDocumentId
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadingDocumentId])

  const handleRemoveDocumentFileToUpload = (documentFilenameToDelete: string) => {
    setDocumentFileToUpload((files) => files.filter((file) => file.name !== documentFilenameToDelete))
  }

  const handleGetDocumentFileSystemName = () => {
    if (selectedCountry === 'COL') {
      if (i18n.language === 'es-ES' || i18n.language === 'es') {
        return {
          bucketName: 'adamo-pay-payments-format',
          documentFileSystemName: 'FormatoAdamoPayColEsp.xlsx'
        }
      } else {
        return {
          bucketName: 'adamo-pay-payments-format',
          documentFileSystemName: 'AdamoPayFormatColEn.xlsx'
        }
      }
    }

    if (selectedCountry === 'MEX') {
      if (i18n.language === 'es-ES' || i18n.language === 'es') {
        return {
          bucketName: 'adamo-pay-payments-format',
          documentFileSystemName: 'FormatoAdamoPayMexEsp.xlsx'
        }
      } else {
        return {
          bucketName: 'adamo-pay-payments-format',
          documentFileSystemName: 'AdamoPayFormatMexEn.xlsx'
        }
      }
    }

    if (selectedCountry === 'ARG') {
      if (i18n.language === 'es-ES' || i18n.language === 'es') {
        return {
          bucketName: 'adamo-pay-payments-format',
          documentFileSystemName: 'FormatoAdamoPayArgEsp.xlsx'
        }
      } else {
        return {
          bucketName: 'adamo-pay-payments-format',
          documentFileSystemName: 'AdamoPayFormatArgEn.xlsx'
        }
      }
    }

    if (selectedCountry === 'CHI') {
      if (i18n.language === 'es-ES' || i18n.language === 'es') {
        return {
          bucketName: 'adamo-pay-payments-format',
          documentFileSystemName: 'FormatoAdamoPayChiEsp.xlsx'
        }
      } else {
        return {
          bucketName: 'adamo-pay-payments-format',
          documentFileSystemName: 'AdamoPayFormatChiEn.xlsx'
        }
      }
    }

    if (selectedCountry === 'PER') {
      if (i18n.language === 'es-ES' || i18n.language === 'es') {
        return {
          bucketName: 'adamo-pay-payments-format',
          documentFileSystemName: 'FormatoAdamoPayPerEsp.xlsx'
        }
      } else {
        return {
          bucketName: 'adamo-pay-payments-format',
          documentFileSystemName: 'AdamoPayFormatPerEn.xlsx'
        }
      }
    }

    if (selectedCountry === 'BRA') {
      if (i18n.language === 'es-ES' || i18n.language === 'es') {
        return {
          bucketName: 'adamo-pay-payments-format',
          documentFileSystemName: 'FormatoAdamoPayBraEsp.xlsx'
        }
      } else {
        return {
          bucketName: 'adamo-pay-payments-format',
          documentFileSystemName: 'AdamoPayFormatBraEn.xlsx'
        }
      }
    }

    return {
      bucketName: 'adamo-pay-payments-format',
      documentFileSystemName: 'AdamoPayFormatColEn.xlsx'
    }
  }

  const handleDownloadAdamoPayFormat = async () => {
    try {
      const documentFileSystemName = handleGetDocumentFileSystemName()

      updateLoadingStatus?.(true)
      const accessToken = await resolveDocumentsAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const documentDownloadPreSignedURLResponse = await getDownloadDocumentPreSignedURL(documentFileSystemName, accessToken)
      window.open(documentDownloadPreSignedURLResponse.data, '_blank')

      updateLoadingStatus?.(false)
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized') })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError') })
      }
      return
    }
  }

  const handleProcessFileToUpload = async () => {
    setErrors({})

    const file = documentFileToUpload[0]
    const currentDate = Date.now()
    const fileToUploadOriginalName = file.name
    const fileToUploadAsBlob = file.slice(0, file.size)
    const fileToUploadName = file.name.replaceAll('...', '')

    const fileToUploadFileSystemName = `payments-${currentDate}.${fileToUploadName.split('.')[fileToUploadName.split('.').length - 1]}`
    const fileToUploadRenamed = new File([fileToUploadAsBlob], fileToUploadFileSystemName, { type: `${file.type}` })

    try {
      setUploadingDocument(true)
      setOpenAnalyzingModal(true)

      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))
      // const databaseExists = await handleCheckIfDatabaseExists()

      // if (databaseExists) {
      const formData = new FormData()
      formData.append('file', fileToUploadRenamed)
      const {
        data: { batchNameAlreadyExists, documentNumOfRows }
      } = await getBatchByName(fileToUploadName, String(userSession?.belongsTo), formData, accessToken)
      // const documentAlreadyExists = currentDocumentsData.find((currentDocumentData) => currentDocumentData.originalName.toLowerCase().trim() === fileToUploadOriginalName.toLowerCase().trim())

      if (batchNameAlreadyExists) {
        setUploadingDocument(false)
        setOpenAnalyzingModal(false)
        setErrors({ generalError: t('formGeneralErrors.documentAlreadyExists') })
        return
      }

      if (documentNumOfRows > 550) {
        setUploadingDocument(false)
        setOpenAnalyzingModal(false)
        setErrors({ generalError: t('formGeneralErrors.documentTooLarge') })
        return
      }
      //}

      const documentFileSystemName = {
        documentFileSystemName: fileToUploadFileSystemName
      }
      const documentUploadedResponse = await getUploadDocumentPreSignedURL(documentFileSystemName, accessToken)
      await uploadDocumentToS3(documentUploadedResponse.data, fileToUploadRenamed)
      const documentBelongsTo = handleGetTheDocumentOwnwer()
      const selectedPocketIdToSet =
        userSession?.role === 'super-admin-client' ? selectedPocketId : userSession?.role === 'admin-client' || userSession?.role === 'admin-client-builder' ? defaultPocketIdSelected : undefined

      const documentToUploadData = {
        currency,
        status: 'uploaded',
        createdAt: new Date(),
        belongsTo: documentBelongsTo,
        uploadedBy: userSession?._id,
        originalName: fileToUploadOriginalName,
        fileSystemName: fileToUploadFileSystemName,
        customerCompanyPocketUsed: selectedPocketIdToSet,
        language: i18n.language === 'es-ES' || i18n.language === 'es' ? 'es' : 'en'
      }

      const documentToAnalyzeSaved = await saveDocumentToAnalyze(documentToUploadData, accessToken)
      setDocumentUploaded(true)

      updateUploadingDocumentId?.(documentToAnalyzeSaved.data._id)
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized') })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError') })
      }

      handleRemoveDocumentFileToUpload(file.name)
      setOpenAnalyzingModal(false)
      return
    }
  }

  const handleGetTheDocumentOwnwer = () => {
    if (userSession?.role.includes('-client')) {
      return userSession.belongsTo
    }

    return 'holdings'
  }

  const handleCloseSession = () => {
    if (removeAuth) {
      removeAuth()
      navigate('/', { replace: true, state: { sessionExpired: true } })
    }
  }

  return (
    <>
      <ProcessStepOne
        errors={errors}
        userSession={userSession as UserSessionType}
        handleSetErrors={(errorsToSet) =>
          setErrors((errorsPrevState) => {
            return {
              ...errorsPrevState,
              ...errorsToSet
            }
          })
        }
        selectedPocketId={selectedPocketId}
        documentUploaded={documentUploaded}
        openAnalyzingModal={openAnalyzingModal}
        handleCleanErrors={() => setErrors({})}
        documentFileToUpload={documentFileToUpload}
        setDocumentFileToUpload={setDocumentFileToUpload}
        handleProcessFileToUpload={() => {
          if (selectedCountry === 'COL' && !nitWarningAccepted) {
            setOpenNitVerificationCodeWarningModal(true)
            return
          }

          handleProcessFileToUpload()
        }}
        handleDownloadAdamoPayFormat={handleDownloadAdamoPayFormat}
        handleDialogAction={() => {
          alreadyRedirectedRef.current = true
          updateUploadingDocumentId?.('')
          navigate('/documents-to-review')
        }}
        handleRemoveDocumentFileToUpload={handleRemoveDocumentFileToUpload}
        handleSetPocketId={(pocketIdToSet: string) => setSelectedPocketId(pocketIdToSet)}
      />
      {openNitVerificationCodeWarningModal && (
        <Dialog
          dialogTitle={t('nitVerificationCodeWarningModal.title')}
          handleDialogOpenState={(openState) => setOpenNitVerificationCodeWarningModal(openState)}
          handleDialogAction={() => {
            if (nitVerificationCodeWarningChecked) {
              globalThis.localStorage.setItem('nitWarningAccepted', 'true')
            }

            setOpenNitVerificationCodeWarningModal(false)
            handleProcessFileToUpload()
          }}
          actionButtonText={t('nitVerificationCodeWarningModal.actionButtonText')}
        >
          <div className="dialog-content-text">
            <div style={{ lineHeight: '150%' }}>{t('nitVerificationCodeWarningModal.instructionFirstLine')}</div>
            <div style={{ lineHeight: '150%' }}>{t('nitVerificationCodeWarningModal.instructionSecondLine')}</div>
            <div style={{ padding: '0px 16px', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Checkbox
                checked={nitVerificationCodeWarningChecked}
                onClick={(event) => {
                  event.stopPropagation()
                  setNitVerificationCodeWarningChecked(!nitVerificationCodeWarningChecked)
                }}
              />
              <div
                className="add-new-bank-account-form-checkbox-text"
                style={{ color: 'rgb(46, 211, 183)', fontWeight: '600' }}
              >
                {t('nitVerificationCodeWarningModal.dontShowAgain')}
              </div>
            </div>
          </div>
        </Dialog>
      )}
    </>
  )
}

export default UploadDocumentProcess
